import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Button } from "@/components/design/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { csrfToken } from "@/auth/csrf";
import { getConfig } from "@/lib/config";
import { userErrorMessage } from "@/lib/user-error";
import { MicrosoftAccountKind, MicrosoftCalendarConnectionDocument, CheckMicrosoftCalendarDocument, DisconnectMicrosoftCalendarDocument } from "@/graphql/generated/operations";
import { useHasPermission, Permission } from "@/auth/access-permissions";
import { MicrosoftCalendarAgenda } from "@/components/microsoft/MicrosoftCalendarAgenda";
import { SettingsCard } from "./SettingsCard";

const statusKeys: Record<string, string> = {
  NOT_CONFIGURED: "microsoft.notConfigured", DISCONNECTED: "microsoft.disconnected",
  CONNECTED: "microsoft.connected", TEAMS_UNAVAILABLE: "microsoft.teamsUnavailable",
  RECONNECT_REQUIRED: "microsoft.reconnectRequired",
};
const outcomeKeys: Record<string, string> = {
  connected: "microsoft.success", cancelled: "microsoft.cancelled", expired: "microsoft.expired", failed: "microsoft.failed", incomplete: "microsoft.completeHint",
};

export function MicrosoftCalendarPanel() {
  const allowed = useHasPermission(Permission.UseMicrosoftConnection);
  return allowed ? <MicrosoftCalendarSettingsContent /> : null;
}

function MicrosoftCalendarSettingsContent() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState(params.get("microsoftAccount") === "COMPANY" ? MicrosoftAccountKind.Company : MicrosoftAccountKind.Personal);
  const [connectionBusy, setConnectionBusy] = useState(false);
  function change(next: MicrosoftAccountKind) {
    setKind(next);
    const query = new URLSearchParams(params); query.set("microsoftAccount", next); query.delete("microsoft");
    setParams(query, { replace: true });
  }
  return <SettingsCard title={t("microsoft.title")} sub={t("microsoft.intro")}>
    <div className="mb-5 space-y-2">
      <SegmentedControl id="microsoft-account-kind" aria-label={t("microsoft.accountKind")} value={kind} size="md"
        onChange={value => change(value as MicrosoftAccountKind)} options={[
          { value: MicrosoftAccountKind.Personal, label: t("microsoft.personal"), disabled: connectionBusy },
          { value: MicrosoftAccountKind.Company, label: t("microsoft.company"), disabled: connectionBusy },
        ]} />
      <p className="text-smycles-meta text-ink-muted">{t("microsoft.connectionSelectionHint")}</p>
    </div>
    <MicrosoftAccountConnection key={kind} accountKind={kind} onBusyChange={setConnectionBusy} />
  </SettingsCard>;
}

function MicrosoftAccountConnection({ accountKind, onBusyChange }: {
  accountKind: MicrosoftAccountKind; onBusyChange: (busy: boolean) => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [params, setParams] = useSearchParams();
  const { data, loading, error, refetch } = useQuery(MicrosoftCalendarConnectionDocument, { variables: { accountKind }, fetchPolicy: "network-only" });
  const [check] = useMutation(CheckMicrosoftCalendarDocument);
  const [disconnect] = useMutation(DisconnectMicrosoftCalendarDocument);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"disconnect" | "replace" | "complete" | null>(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  const connection = data?.microsoftCalendarConnection;
  const incomplete = connection?.connected && !connection.mailAvailable;
  const statusTone = error || failure || connection?.state === "RECONNECT_REQUIRED" ? "error"
    : busy || loading || incomplete || connection?.state !== "CONNECTED" ? "warning" : "success";
  const statusLabel = error || failure ? "unconfirmed" : busy || loading ? "checking"
    : connection?.state === "RECONNECT_REQUIRED" ? "reconnect" : incomplete ? "incomplete"
    : connection?.state === "CONNECTED" ? "connected" : connection?.connected ? "limited" : "disconnected";
  const statusDot = statusTone === "success" ? "bg-emerald-500" : statusTone === "error" ? "bg-rose-500" : "bg-amber-500";
  const marker = params.get("microsoft");
  const outcome = marker ? outcomeKeys[marker] : undefined;

  function clearFeedback() {
    setFailure(null); setChecked(false);
    if (marker) { const next = new URLSearchParams(params); next.delete("microsoft"); setParams(next, { replace: true }); }
  }
  async function connect() {
    setConfirm(null);
    clearFeedback(); setBusy(true);
    try {
      const response = await fetch(`${getConfig().basePath}auth/microsoft/start`, {
        method: "POST", credentials: "include", headers: { "X-CSRF-Token": csrfToken() ?? "", "Content-Type": "application/json" }, body: JSON.stringify({ accountKind }),
      });
      if (!response.ok) throw new Error("Microsoft connection unavailable");
      const result = await response.json() as { url: string };
      const url = new URL(result.url);
      if (url.origin !== "https://login.microsoftonline.com") throw new Error("Invalid Microsoft redirect");
      window.location.assign(url.toString());
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); setBusy(false); }
  }
  async function verify() {
    clearFeedback(); setBusy(true);
    try { await check({ variables: { accountKind } }); await refetch(); setChecked(true); }
    catch (cause) { setFailure(userErrorMessage(intl, cause)); }
    finally { setBusy(false); }
  }
  async function unlink() {
    if (busy) return;
    clearFeedback(); setBusy(true); setConfirm(null);
    try { await disconnect({ variables: { accountKind } }); await refetch(); }
    catch (cause) { setFailure(userErrorMessage(intl, cause)); }
    finally { setBusy(false); }
  }
  return <>
    <div className="space-y-4" aria-busy={busy || loading}>
      {loading && !connection ? <p role="status" className="text-smycles-body text-ink-muted">{t("microsoft.loading")}</p> : null}
      {error || failure ? <p role="alert" className="text-smycles-body text-ink">{failure ?? userErrorMessage(intl, error)}</p> : null}
      {outcome && !failure && !incomplete ? <p role="status" className="text-smycles-body text-ink">{t(outcome)}</p> : null}
      {connection ? <>
        <div className="space-y-2">
          <span id="microsoft-status-pill" role="status" data-tone={statusTone}
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-3 py-1 text-smycles-meta text-ink">
            <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDot}`} />
            {t(`microsoft.status.${statusLabel}`)}
          </span>
          <p id="microsoft-status" className="text-smycles-meta text-ink-muted">{t(connection.state !== "RECONNECT_REQUIRED" && incomplete ? "microsoft.completeHint" : statusKeys[connection.state] ?? "microsoft.failed")}</p>
        </div>
        {connection.connected ? <div>
          <p className="text-smycles-body font-medium">{connection.displayName}</p>
          <p id="microsoft-account" className="text-smycles-meta text-ink-muted">{connection.email}</p>
          {connection.checkedAt ? <p className="mt-2 text-smycles-meta text-ink-muted">
            {intl.formatMessage({ id: "microsoft.checkedAt" }, { time: intl.formatDate(connection.checkedAt, { dateStyle: "medium", timeStyle: "short" }) })}
          </p> : null}
        </div> : null}
        <div className="flex flex-wrap gap-2">
          <Button id="microsoft-connect" variant="microsoft" disabled={busy || !connection.configured} onClick={() => incomplete ? setConfirm("complete") : connection.connected ? setConfirm("replace") : void connect()}>
            {t("microsoft.signIn")}
          </Button>
          {connection.connected ? <>
            <Button id="microsoft-check" variant="secondary" disabled={busy || !connection.configured} onClick={() => void verify()}>{t("microsoft.check")}</Button>
            <Button id="microsoft-disconnect" variant="ghost" disabled={busy} onClick={() => setConfirm("disconnect")}>{t("microsoft.disconnect")}</Button>
          </> : null}
        </div>
        {checked ? <p role="status" className="text-smycles-meta text-ink-muted">{t("microsoft.checked")}</p> : null}
        {connection.configured && connection.connected && connection.mailAvailable && connection.email
          && connection.state !== "RECONNECT_REQUIRED" && !busy && !loading && !error ?
          <MicrosoftCalendarAgenda key={connection.email} accountKind={accountKind} organizer={connection.email} start="" end="" /> : null}
      </> : null}
      {error && !connection ? <Button variant="secondary" disabled={busy} onClick={() => void refetch()}>{t("microsoft.retry")}</Button> : null}
    </div>
    <ConfirmDialog id="microsoft-disconnect-confirmation" open={confirm !== null} title={t(confirm === "complete" ? "microsoft.complete" : confirm === "replace" ? "microsoft.changeAccount" : "microsoft.disconnect")}
      message={t(confirm === "complete" ? "microsoft.completeMessage" : confirm === "replace" ? "microsoft.replaceMessage" : "microsoft.disconnectMessage")}
      destructive={confirm === "disconnect"} confirmLabel={t(confirm === "complete" ? "microsoft.continueToMicrosoft" : confirm === "replace" ? "microsoft.changeAccount" : "microsoft.disconnect")}
      onConfirm={confirm === "disconnect" ? unlink : connect} onCancel={() => setConfirm(null)} />
  </>;
}
